import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, CircularProgress,
  Paper, Avatar, Chip,
} from '@mui/material';
import { Send as SendIcon, Chat as ChatIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const POLL_INTERVAL = 30000; // 30 seconds

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatDate = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const groupByDay = (messages) => {
  const groups = [];
  let lastDay = null;
  for (const msg of messages) {
    const day = new Date(msg.createdAt).toDateString();
    if (day !== lastDay) {
      groups.push({ type: 'date', label: formatDate(msg.createdAt), key: `date-${day}` });
      lastDay = day;
    }
    groups.push({ type: 'message', ...msg });
  }
  return groups;
};

const JobChat = ({ jobId, compact = false }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async (silent = false) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/jobs/${jobId}/messages`, {
        headers: authHeaders(),
      });
      setMessages(data);
      if (!silent) setLoading(false);
    } catch (err) {
      logger.error('JobChat fetch error:', err);
      if (!silent) setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(() => fetchMessages(true), POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data: newMsg } = await axios.post(
        `${API_URL}/api/jobs/${jobId}/messages`,
        { body: text },
        { headers: authHeaders() }
      );
      setMessages((prev) => [...prev, newMsg]);
      setBody('');
    } catch (err) {
      logger.error('JobChat send error:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isMine = (msg) => msg.senderId === user?.id;

  const grouped = groupByDay(messages);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: compact ? 360 : '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <ChatIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={600}>Messages</Typography>
        {messages.length > 0 && (
          <Chip label={messages.length} size="small" sx={{ ml: 'auto', height: 20, fontSize: 11 }} />
        )}
      </Box>

      {/* Message list */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" pt={3}>
            <CircularProgress size={24} />
          </Box>
        ) : grouped.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={3}>
            No messages yet. Start the conversation.
          </Typography>
        ) : (
          grouped.map((item) =>
            item.type === 'date' ? (
              <Box key={item.key} display="flex" alignItems="center" my={1.5} gap={1}>
                <Box flex={1} height="1px" bgcolor="divider" />
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                <Box flex={1} height="1px" bgcolor="divider" />
              </Box>
            ) : (
              <Box
                key={item.id}
                display="flex"
                flexDirection={isMine(item) ? 'row-reverse' : 'row'}
                alignItems="flex-end"
                gap={1}
                mb={1}
              >
                {!isMine(item) && (
                  <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
                    {item.sender?.firstName?.[0]}{item.sender?.lastName?.[0]}
                  </Avatar>
                )}
                <Box maxWidth="75%">
                  {!isMine(item) && (
                    <Typography variant="caption" color="text.secondary" ml={0.5}>
                      {item.sender?.firstName} · {item.senderRole}
                    </Typography>
                  )}
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: isMine(item) ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                      bgcolor: isMine(item) ? 'primary.main' : 'grey.100',
                      color: isMine(item) ? 'primary.contrastText' : 'text.primary',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {item.body}
                    </Typography>
                  </Paper>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    display="block"
                    textAlign={isMine(item) ? 'right' : 'left'}
                    ml={0.5}
                  >
                    {formatTime(item.createdAt)}
                    {isMine(item) && item.readAt && ' · Read'}
                  </Typography>
                </Box>
              </Box>
            )
          )
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Input */}
      <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={3}
          placeholder="Type a message…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKey}
          disabled={sending}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!body.trim() || sending}
          sx={{ alignSelf: 'flex-end' }}
        >
          {sending ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default JobChat;
